<script setup lang="ts">
import { computed } from 'vue'
import { IDEVIEWTABS } from '@/utils/tabs'
import { useIdeStore } from '@/stores/ide.store'
import editorService from '@/services/ide/editor.service'
import { useTabStore } from '@/stores/tabs.store'
import tabsService from '@/services/ide/tabs.service'
import { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import SideBarTooltip from '@/components/ide/ide/SettingsSideBarTooltip.vue'
import TabsSettingActive from '@/components/ide/ide/TabsSettingActive.vue'
import excuteLiberaryIcon from '@/assets/images/ide/folder-down.svg'
import uploadFilesIcon from '@/assets/images/ide/upload.svg'
import inputOutputIcon from '@/assets/images/ide/device-desktop-code.svg'
import JDroidIcon from '@/assets/images/ide/JDroidIconLatest.svg'
import inputOutputActiveIcon from '@/assets/images/ide/device-desktop-code-selected.svg'
import excuteLiberaryActiveIcon from '@/assets/images/ide/folder-down-selected.svg'
import uploadFilesActiveIcon from '@/assets/images/ide/upload-selected.svg'
import browserIcon from '@/assets/images/ide/browser.svg'
import browserIconDark from '@/assets/images/ide/browserdark.svg'
import browserActiveIcon from '@/assets/images/ide/browser-selected.svg'
import inputOutputIconDark from '@/assets/images/ide/iodark.svg'
import excuteLiberaryIconDark from '@/assets/images/ide/exlidark.svg'
import uploadFilesIconDark from '@/assets/images/ide/uploadResdark.svg'
import dbTerminalIcon from '@/assets/images/ide/terminaldb.svg'
import dbTerminalDarkIcon from '@/assets/images/ide/terminaldbDark.svg'
import dbTerminalActiveIcon from '@/assets/images/ide/terminaldbActive.svg'
import apiBrowserIcon from '@/assets/images/ide/apiBrowserIcon.svg'
import apiBrowserDarkIcon from '@/assets/images/ide/apiBrowserIconDark.svg'
import apiBrowserActiveIcon from '@/assets/images/ide/apiBrowserIconActiveIcon.svg'
import terminalIcon from '@/assets/images/ide/terminalIcon.svg'
import terminalDarkIcon from '@/assets/images/ide/terminalIconDark.svg'
import terminalActiveIcon from '@/assets/images/ide/terminalIconActiveIcon.svg'
import consoleIcon from '@/assets/images/ide/consoleIcon.svg'
import consoleDarkIcon from '@/assets/images/ide/consoleIconDark.svg'
import consoleActiveIcon from '@/assets/images/ide/consoleIconActiveIcon.svg'

const props = defineProps({
  hideRightBar: {
    type: Boolean,
    required: false
  }
})

const ideStore = useIdeStore()
const tabStore = useTabStore()

/**
 * Returns the appropriate icon based on tab visibility and active state.
 * @param tabName - The name of the tab to check.
 * @param activeIcon - The icon to return if the tab is active.
 * @param darkActiveIcon - The dark icon to return if the tab is active (for dark theme).
 * @param defaultIcon - The default icon to return if the tab is not active.
 * @returns  An object containing the light and dark icons.
 */
const getTabIcons = (
  tabName: TAB_ICONS_NAMES,
  activeIcon: string,
  darkActiveIcon: string,
  defaultIcon: string
) => {
  const checkActive = tabsService.checkRightTab(tabName, true)

  return {
    light: checkActive ? activeIcon : defaultIcon,
    dark: checkActive ? darkActiveIcon : defaultIcon
  }
}

const ioTabIcons = computed(() =>
  getTabIcons(TAB_ICONS_NAMES.IO, inputOutputActiveIcon, inputOutputIconDark, inputOutputIcon)
)

const externalLiTabIcons = computed(() =>
  getTabIcons(
    TAB_ICONS_NAMES.EXTERNAL_LIBRARIES,
    excuteLiberaryActiveIcon,
    excuteLiberaryIconDark,
    excuteLiberaryIcon
  )
)

const uploadResTabIcons = computed(() =>
  getTabIcons(
    TAB_ICONS_NAMES.UPLOAD_FILES,
    uploadFilesActiveIcon,
    uploadFilesIconDark,
    uploadFilesIcon
  )
)

const browserTabIcons = computed(() =>
  getTabIcons(TAB_ICONS_NAMES.BROWSER, browserActiveIcon, browserIconDark, browserIcon)
)

const apiTabIcons = computed(() =>
  getTabIcons(TAB_ICONS_NAMES.API_TAB, apiBrowserActiveIcon, apiBrowserDarkIcon, apiBrowserIcon)
)

const dbTerminalTabIcons = computed(() =>
  getTabIcons(TAB_ICONS_NAMES.DB_TERMINAL, dbTerminalActiveIcon, dbTerminalDarkIcon, dbTerminalIcon)
)

const terminalTabIcons = computed(() =>
  getTabIcons(TAB_ICONS_NAMES.TERMINAL, terminalActiveIcon, terminalDarkIcon, terminalIcon)
)

const consoleTabIcons = computed(() =>
  getTabIcons(TAB_ICONS_NAMES.CONSOLE, consoleActiveIcon, consoleDarkIcon, consoleIcon)
)

const showIORef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.IO)
})
const showExtLibRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.EXTERNAL_LIBRARIES)
})
const showUploadResRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.UPLOAD_FILES)
})
const showJroidRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.JDROID)
})

const showBrowserRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.BROWSER)
})

const showDbTerminalRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.DB_TERMINAL)
})

const showApiTabRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.API_TAB)
})

const showTerminalRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.TERMINAL)
})

const showConsoleRef = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.CONSOLE)
})

const isDefaultIde = computed(() => {
  return ideStore.isDefaultIde
})

const isShowLibs = computed(() => {
  return ideStore.ideMeta?.isPlus && ideStore.ideMeta?.library
})

const showIORefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.IO, true)
})
const showExtLibRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.EXTERNAL_LIBRARIES, true)
})
const showUploadResRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.UPLOAD_FILES, true)
})
const showJroidRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.JDROID, true)
})

const showBrowserRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.BROWSER, true)
})

const showDbTerminalRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.DB_TERMINAL, true)
})

const showApiTabRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.API_TAB, true)
})

const showTerminalRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.TERMINAL, true)
})

const showConsoleRefView = computed(() => {
  return tabsService.checkRightTab(TAB_ICONS_NAMES.CONSOLE, true)
})

/**
 * Updates the visibility of a tab by setting its icon position.
 * @param  tabName - The name of the tab to update.
 */
const updateActiveState = (tabName: string) => {
  if (tabName == TAB_ICONS_NAMES.IO) {
    refreshOutputEditor()
  }
  handleActiveTabState(tabName)
}

/**
 * Refreshes the output editor when come back to output tab after execute
 */
const refreshOutputEditor = async () => {
  if (ideStore.outputEditor) {
    await new Promise((resolve) => setTimeout(resolve, 10))
    editorService.resizeOutputEditor()
  }
}

/**
 *
 * @param name name of tab to update
 */
const handleActiveTabState = async (name: string) => {
  if (name == TAB_ICONS_NAMES.IO) {
    ideStore.setHasErrorAfterExecute(false)
  }

  tabStore.setActiveTabPosition(TAB_BUTTON_POSITION.RIGHT, name as TAB_ICONS_NAMES)
}
</script>

<template>
  <div
    class="background-quaternary stroke-primary-default z-20 flex h-full"
    :class="!props.hideRightBar ? 'border-l' : ''"
  >
    <div
      class="flex gap-2.5"
      :class="
        props.hideRightBar ? 'max-h-[42px] w-full flex-row' : 'max-h-[480px] w-[42px] flex-col'
      "
    >
      <div>
        <nav
          aria-label="Tabs"
          role="tablist"
          :class="props.hideRightBar ? ' flex flex-row items-center' : 'flex flex-col items-center'"
        >
          <div
            v-if="showJroidRef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showJroidRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.JDROID}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.JDROID}`"
              :aria-controls="`${IDEVIEWTABS.JDROID}`"
              :disabled="isDefaultIde"
              @click="handleActiveTabState(TAB_ICONS_NAMES.JDROID)"
            >
              <img :src="JDroidIcon" />
            </button>
            <div v-if="props.hideRightBar && showJroidRefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.JDROID"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="JDroid AI"
              />
            </div>
            <SideBarTooltip content="JDroid" />
          </div>

          <div
            v-if="showIORef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showIORefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :disabled="isDefaultIde"
              :id="`${IDEVIEWTABS.IO}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.IO}`"
              :aria-controls="`${IDEVIEWTABS.IO}`"
              @click="updateActiveState(TAB_ICONS_NAMES.IO)"
            >
              <img :src="ioTabIcons.light" class="block dark:hidden" />
              <img :src="ioTabIcons.dark" class="hidden dark:block" />
            </button>

            <div v-if="showIORefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.IO"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="Input/Output"
              />
            </div>

            <SideBarTooltip content="Input/Output" />
          </div>

          <div
            v-if="showExtLibRef && isShowLibs"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showExtLibRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.LIBRARIES}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.LIBRARIES}`"
              :aria-controls="`${IDEVIEWTABS.LIBRARIES}`"
              :disabled="isDefaultIde"
              @click="updateActiveState(TAB_ICONS_NAMES.EXTERNAL_LIBRARIES)"
            >
              <img :src="externalLiTabIcons.light" class="block dark:hidden" />
              <img :src="externalLiTabIcons.dark" class="hidden dark:block" />
            </button>
            <div v-if="props.hideRightBar && showExtLibRefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.EXTERNAL_LIBRARIES"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="External libraries"
              />
            </div>
            <SideBarTooltip content="External libraries" />
          </div>

          <div
            v-if="showUploadResRef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showUploadResRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.INPUTFILES}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.INPUTFILES}`"
              :aria-controls="`${IDEVIEWTABS.INPUTFILES}`"
              :disabled="isDefaultIde"
              @click="updateActiveState(TAB_ICONS_NAMES.UPLOAD_FILES)"
            >
              <img :src="uploadResTabIcons.light" class="block dark:hidden" />
              <img :src="uploadResTabIcons.dark" class="hidden dark:block" />
            </button>
            <div v-if="props.hideRightBar && showUploadResRefView" class="relative ml-1">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.UPLOAD_FILES"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="Upload input files"
              />
            </div>
            <SideBarTooltip content="Upload input files" />
          </div>
          <div
            v-if="showBrowserRef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showBrowserRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.BROWSER}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.BROWSER}`"
              :aria-controls="`${IDEVIEWTABS.BROWSER}`"
              :disabled="isDefaultIde"
              @click="updateActiveState(TAB_ICONS_NAMES.BROWSER)"
            >
              <img :src="browserTabIcons.light" class="block dark:hidden" />
              <img :src="browserTabIcons.dark" class="hidden dark:block" />
            </button>
            <div v-if="props.hideRightBar && showBrowserRefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.BROWSER"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="Browser"
              />
            </div>
            <SideBarTooltip content="Browser" />
          </div>
          <div
            v-if="showApiTabRef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showApiTabRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.API_TAB}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.API_TAB}`"
              :aria-controls="`${IDEVIEWTABS.API_TAB}`"
              :disabled="isDefaultIde"
              @click="updateActiveState(TAB_ICONS_NAMES.API_TAB)"
            >
              <img :src="apiTabIcons.light" class="block dark:hidden" />
              <img :src="apiTabIcons.dark" class="hidden dark:block" />
            </button>
            <div v-if="props.hideRightBar && showApiTabRefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.API_TAB"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="API tester"
              />
            </div>
            <SideBarTooltip content="API tester" />
          </div>

          <div
            v-if="showDbTerminalRef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showDbTerminalRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.DB_TERMINAL}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.DB_TERMINAL}`"
              :aria-controls="`${IDEVIEWTABS.DB_TERMINAL}`"
              :disabled="isDefaultIde"
              @click="updateActiveState(TAB_ICONS_NAMES.DB_TERMINAL)"
            >
              <img :src="dbTerminalTabIcons.light" class="block dark:hidden" />
              <img :src="dbTerminalTabIcons.dark" class="hidden dark:block" />
            </button>
            <div v-if="props.hideRightBar && showDbTerminalRefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.DB_TERMINAL"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="Console"
              />
            </div>
            <SideBarTooltip content="Console" />
          </div>

          <div
            v-if="showTerminalRef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showTerminalRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.TERMINAL}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.TERMINAL}`"
              :aria-controls="`${IDEVIEWTABS.TERMINAL}`"
              :disabled="isDefaultIde"
              @click="updateActiveState(TAB_ICONS_NAMES.TERMINAL)"
            >
              <img :src="terminalTabIcons.light" class="block dark:hidden" />
              <img :src="terminalTabIcons.dark" class="hidden dark:block" />
            </button>
            <div v-if="props.hideRightBar && showTerminalRefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.TERMINAL"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="Terminal"
              />
            </div>
            <SideBarTooltip content="Terminal" />
          </div>

          <div
            v-if="showConsoleRef"
            class="hs-tooltip flex items-center px-2 py-2 [--placement:left]"
            :class="showConsoleRefView ? 'surface-secondary-base' : ''"
          >
            <button
              type="button"
              role="tab"
              class="hs-tooltip-toggle active h-5 w-5"
              :id="`${IDEVIEWTABS.CONSOLE}-item`"
              :data-hs-tab="`#${IDEVIEWTABS.CONSOLE}`"
              :aria-controls="`${IDEVIEWTABS.CONSOLE}`"
              :disabled="isDefaultIde"
              @click="updateActiveState(TAB_ICONS_NAMES.CONSOLE)"
            >
              <img :src="consoleTabIcons.light" class="block dark:hidden" />
              <img :src="consoleTabIcons.dark" class="hidden dark:block" />
            </button>
            <div v-if="props.hideRightBar && showConsoleRefView" class="relative ml-2">
              <TabsSettingActive
                :tabName="TAB_ICONS_NAMES.CONSOLE"
                :currentPosition="TAB_BUTTON_POSITION.RIGHT"
                tabDisplayName="Console"
              />
            </div>
            <SideBarTooltip content="Console" />
          </div>
        </nav>
      </div>
    </div>
  </div>
</template>
